import { useCallback } from 'react';

import { useUpdateBacker } from '@givebacks/api/backer';
import { useListUserCauseSelections } from '@givebacks/api/core';
import { ClickableOptionsCard } from 'core/Components';
import { useNavigate } from 'react-router-dom';
import {
  ButtonMantine,
  Card,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
  ThemeIcon,
  notifications,
} from 'ui';
import { IconBling, IconPlus } from 'ui/Icons';

import { OnboardingModal } from '../OnboardingModal';
import { YourFollowing } from './YourFollowing';

const CausesFollowingModalFooter = () => {
  const { data: userCauseSelections } = useListUserCauseSelections();

  const { mutate: updateBacker, isPending: isUpdatingBacker } = useUpdateBacker();
  const navigate = useNavigate();

  const handleStartSupporting = useCallback(() => {
    if (userCauseSelections && userCauseSelections.length > 0) {
      updateBacker(
        { backer: { onboarding_complete: true } },
        {
          onSettled: () => {
            navigate('/home/onboarding/your-personalized-start');
          },
        }
      );
    } else {
      notifications.show({
        message: 'Please select at least one cause to start supporting',
        variant: 'error',
      });
    }
  }, [userCauseSelections, updateBacker, navigate]);

  return (
    <Modal.AffixedFooter>
      <Stack gap="md" mt="lg">
        <Card radius="12px" bg="gray.0.5">
          <Group wrap="nowrap" gap="xs" align="start">
            <ThemeIcon variant="transparent" size="sm">
              <IconBling />
            </ThemeIcon>
            <Text fz="sm" c="gray.8">
              <Text fw={500} fz="sm">
                Did you know...
              </Text>
              <Text fz="xs">
                There are more than 1 million nonprofits—local and national—waiting for your
                support!
              </Text>
            </Text>
          </Group>
        </Card>
        <ButtonMantine size="lg" onClick={handleStartSupporting} loading={isUpdatingBacker}>
          Continue
        </ButtonMantine>
      </Stack>
    </Modal.AffixedFooter>
  );
};

const FindAnotherOrg = () => {
  const navigate = useNavigate();

  return (
    <ClickableOptionsCard>
      <ClickableOptionsCard.Row
        title="Find another org or school"
        onClick={() => navigate('/onboarding/causes')}
        groupProps={{ bg: 'cobalt.0.5' }}
        rightSection={
          <ThemeIcon variant="transparent" color="cobalt.6">
            <IconPlus />
          </ThemeIcon>
        }
      />
    </ClickableOptionsCard>
  );
};

const CausesFollowingContent = () => {
  const { isFetching: isFetchingUserCauseSelections } = useListUserCauseSelections();

  if (isFetchingUserCauseSelections) {
    return <LoadingOverlay visible={true} />;
  }

  return (
    <Stack w="100%" pt="xl" gap="lg">
      <YourFollowing />
      <FindAnotherOrg />
    </Stack>
  );
};

const CausesFollowingModal = () => {
  return (
    <OnboardingModal
      title="Are these the orgs you want to support?"
      description="Take a moment to review or update your favorite organizations before moving on."
    >
      <CausesFollowingContent />
      <CausesFollowingModalFooter />
    </OnboardingModal>
  );
};

export { CausesFollowingModal };
